import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import DashboardLayout from "@/components/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Board from "@/pages/Board";
import Research from "@/pages/Research";
import Odds from "@/pages/Odds";
import Weather from "@/pages/Weather";
import LiveHR from "@/pages/LiveHR";
import Pitchers from "@/pages/Pitchers";
import WeakSpots from "@/pages/WeakSpots";
import BvP from "@/pages/BvP";
import HRRTargets from "@/pages/HRRTargets";
import BatterZone from "@/pages/BatterZone";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import EVCalc from "@/pages/EVCalc";
import Parlay from "@/pages/Parlay";
import HitTracker from "@/pages/HitTracker";
import Watchlist from "@/pages/Watchlist";
import Settings from "@/pages/Settings";
import Community from "@/pages/Community";
import ComingSoon from "@/pages/ComingSoon";
import LikelyHR from "@/pages/LikelyHR";
import GameLines from "@/pages/GameLines";
import MatchupLab from "@/pages/MatchupLab";
import HeatBoard from "@/pages/HeatBoard";
import HRSimulator from "@/pages/HRSimulator";
import LikelyCash from "@/pages/LikelyCash";
import Soccer from "@/pages/Soccer";
import PropsThatPay from "@/pages/PropsThatPay";
import TeamMatchup from "@/pages/TeamMatchup";
import NFL from "@/pages/NFL";
import Admin from "@/pages/Admin";

const Loader = () => (
  <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
    <div className="font-display text-2xl font-bold text-[#a855f7] uppercase tracking-widest animate-pulse">
      Loading the sheet...
    </div>
  </div>
);

function Protected() {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (user === false) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function AdminOnly() {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route element={<Protected />}>
            <Route element={<DashboardLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/coming-soon/:sport" element={<ComingSoon />} />
              <Route path="/nfl" element={<NFL />} />
              <Route path="/nfl/:tab" element={<NFL />} />
              <Route path="/ncaaf" element={<NFL />} />
              <Route path="/ncaaf/:tab" element={<NFL />} />
              <Route path="/nba" element={<NFL />} />
              <Route path="/nba/:tab" element={<NFL />} />
              <Route path="/nhl" element={<NFL />} />
              <Route path="/nhl/:tab" element={<NFL />} />
              <Route path="/board" element={<Board />} />
              <Route path="/research" element={<Research />} />
              <Route path="/research/:mlbId" element={<Research />} />
              <Route path="/odds" element={<Odds />} />
              <Route path="/weather" element={<Weather />} />
              <Route path="/live-hr" element={<LiveHR />} />
              <Route path="/pitchers" element={<Pitchers />} />
              <Route path="/weak-spots" element={<WeakSpots />} />
              <Route path="/bvp" element={<BvP />} />
              <Route path="/hrr" element={<HRRTargets />} />
              <Route path="/zone" element={<BatterZone />} />
              <Route path="/likely-hr" element={<LikelyHR />} />
              <Route path="/lines" element={<GameLines />} />
              <Route path="/matchup-lab" element={<MatchupLab />} />
              <Route path="/heat-board" element={<HeatBoard />} />
              <Route path="/hr-sim" element={<HRSimulator />} />
              <Route path="/likely-cash" element={<LikelyCash league="mlb" standalone />} />
              <Route path="/props-pay" element={<PropsThatPay league="mlb" standalone />} />
              <Route path="/team-compare" element={<TeamMatchup league="mlb" standalone />} />
              <Route path="/soccer" element={<Soccer />} />
              <Route path="/soccer/:tab" element={<Soccer />} />
              <Route path="/confidence" element={<Navigate to="/likely-hr" replace />} />
              <Route path="/ev" element={<EVCalc />} />
              <Route path="/parlay" element={<Parlay />} />
              <Route path="/hit-tracker" element={<HitTracker />} />
              <Route path="/watchlist" element={<Watchlist />} />
              <Route path="/community" element={<Community />} />
              <Route path="/settings" element={<Settings />} />
              <Route element={<AdminOnly />}>
                <Route path="/admin" element={<Admin />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
